import {
  getCustomerInvite,
  InvoiceStatusNames,
  useAppSelector,
  useLazyGetInvoiceListQuery,
} from '@zonofi/zono-money-store';
import { debounce, startCase } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUrlDateRange } from '@zonofi/seller-hub/hooks/use-url-date-range';
import { useCustomDateRange } from '@zonofi/seller-hub/hooks/use-custom-date-range';
import moment from 'moment';
import {
  isToday,
  isYesterday,
  usePaginatedList,
} from '@zonofi/zono-money-whatsapp-stack';

export const useInvoiceScreen = () => {
  const inviteData = useAppSelector(getCustomerInvite);
  const { dateRangeFromUrl: appliedDateRange } = useUrlDateRange();
  const { applyNewDateRange } = useCustomDateRange();
  const [searchKey, setSearchKey] = useState('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState({
    show: false,
    code: null,
    message: null,
  });
  const [filterPage, setFilterPage] = useState<string>('');
  const [getInvoiceListAPI, getInvoiceListAPIResponse] =
    useLazyGetInvoiceListQuery();
  const openFiltersDrawer = (filterPage: string) => {
    setFilterPage(filterPage);
  };
  const memoizedRange = useMemo(
    () => ({
      startDate: appliedDateRange?.startDate,
      endDate: appliedDateRange?.endDate,
    }),
    [appliedDateRange?.startDate, appliedDateRange?.endDate]
  );
  const appliedFilters = useMemo(() => {
    const isAppliedDateToday = isToday(
      memoizedRange.startDate,
      memoizedRange.endDate
    );
    const isAppliedDateYesterday = isYesterday(
      memoizedRange.startDate,
      memoizedRange.endDate
    );

    const dateFilter = isAppliedDateToday
      ? 'Today'
      : isAppliedDateYesterday
        ? 'Yesterday'
        : `${moment(memoizedRange.startDate).format('DD-MM-YYYY')} to ${moment(memoizedRange.endDate).format('DD-MM-YYYY')}`;
    return [dateFilter];
  }, [memoizedRange]);
  const debouncedFunction = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchKey(value);
      }, 500),
    []
  );

  useEffect(() => {
    if (searchKey) {
      setIsSearching(true);
    }
    debouncedFunction(searchKey);
  }, [searchKey, debouncedFunction]);
  const statusChips = useMemo(() => {
    return [
      {
        label: 'All',
        value: '',
        onClick: () => {
          setAppliedStatusFilter('');
        },
      },
      ...Object.entries(InvoiceStatusNames).map(([key, value]) => ({
        label: startCase(value),
        value: key,
        onClick: () => {
          setAppliedStatusFilter(key);
        },
      })),
    ];
  }, []);

  const fetchInvoiceList = useCallback(
    ({ pageNo, pageSize }: { pageNo: number; pageSize: number }) => {
      getInvoiceListAPI({
        workspaceId: inviteData?.seller?.workspaceId,
        inviteId: inviteData?.inviteId,
        pageNo,
        pageSize,
        startDate: `${moment(appliedDateRange?.startDate, 'DD/MM/YYYY').format(
          'YYYY-MM-DD'
        )} 23:59:59`,
        endDate: `${moment(appliedDateRange?.endDate, 'DD/MM/YYYY').format(
          'YYYY-MM-DD'
        )} 23:59:59`,
        sortBy: 'createdDate',
        ...(appliedStatusFilter
          ? { invoiceStatus: [appliedStatusFilter] }
          : {}),
        dispatchFilter: true,
        ...(searchKey &&
          searchKey?.length && { searchKey: debouncedSearchKey }),
      });
    },
    [appliedDateRange, appliedStatusFilter, debouncedSearchKey]
  );

  const memoizedPaymentData = useMemo(() => {
    return getInvoiceListAPIResponse?.data?.invoices ?? [];
  }, [getInvoiceListAPIResponse?.data?.invoices]);

  const {
    data: invoicesData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    isEndOfList,
  } = usePaginatedList<any>({
    fetchDataApiCall: fetchInvoiceList,
    apiResponseData: memoizedPaymentData,
    isLoading: getInvoiceListAPIResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [
      appliedDateRange?.startDate.toString(),
      appliedDateRange?.endDate.toString(),
      appliedStatusFilter,
      debouncedSearchKey,
    ],
    isError: getInvoiceListAPIResponse.isError,
    paginationMeta: {
      startRecord: getInvoiceListAPIResponse?.data?.startRecord || 0,
      endRecord: getInvoiceListAPIResponse?.data?.endRecord || 0,
      totalCount: getInvoiceListAPIResponse?.data?.totalInvoices || 0,
    },
  });
  console.log(
    'invoicesData: ',
    invoicesData,
    'isEndOfList: ',
    isEndOfList,
    'pageNo: ',
    pageNo,
    'isLoading',
    pageNo === 1 &&
      (getInvoiceListAPIResponse?.isUninitialized ||
        getInvoiceListAPIResponse?.isFetching ||
        isSearching)
  );
  return {
    showSearch,
    setShowSearch,
    searchKey,
    setSearchKey,
    error,
    setError,
    pageNo,
    onScroll,
    lastItemRef,
    isPageLoading,
    statusChips,
    appliedStatusFilter,
    filterPage,
    setFilterPage,
    openFiltersDrawer,
    appliedDateRange,
    applyNewDateRange,
    appliedFilters,
    invoicesData,
    isLoading:
      pageNo === 1 &&
      (getInvoiceListAPIResponse?.isUninitialized ||
        getInvoiceListAPIResponse?.isFetching),
    isEndOfList,
    getInvoiceListAPIResponse,
  };
};
